import React, { useState, useEffect, useCallback } from 'react';
import './Photos.css';

const BUCKET_URL = 'https://echohill-photos.s3.us-west-1.amazonaws.com';
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'gif'];

function parseS3XML(xml) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xml, 'application/xml');
  const contents = Array.from(doc.querySelectorAll('Contents'));
  return contents
    .map(el => el.querySelector('Key')?.textContent)
    .filter(key => {
      const ext = key?.split('.').pop().toLowerCase();
      return IMAGE_EXTENSIONS.includes(ext);
    });
}

export default function Photos() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    console.log('[Photos] Fetching:', `${BUCKET_URL}/?list-type=2`);
    fetch(`${BUCKET_URL}/?list-type=2`)
      .then(res => {
        console.log('[Photos] Response status:', res.status, res.ok);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then(xml => {
        console.log('[Photos] XML received, length:', xml.length);
        const keys = parseS3XML(xml);
        console.log('[Photos] Parsed keys:', keys);
        setPhotos(keys);
        setLoading(false);
      })
      .catch(err => {
        console.error('[Photos] Fetch error:', err);
        setError(true);
        setLoading(false);
      });
  }, []);

  const handleKey = useCallback((e) => {
    if (lightbox === null) return;
    if (e.key === 'ArrowRight') setLightbox(i => Math.min(i + 1, photos.length - 1));
    if (e.key === 'ArrowLeft')  setLightbox(i => Math.max(i - 1, 0));
    if (e.key === 'Escape')     setLightbox(null);
  }, [lightbox, photos.length]);

  useEffect(() => {
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  useEffect(() => {
    document.body.style.overflow = lightbox !== null ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [lightbox]);

  return (
    <div>
      <div className="section-container">
        <h1 className="section-title">Photos</h1>
        <div className="section-rule" />

        {loading && (
          <div className="photos-status">
            <div className="photos-spinner" />
            <p>Loading photos...</p>
          </div>
        )}

        {error && (
          <div className="photos-status photos-status--warn">
            <p>Could not load photos right now. Please try again later.</p>
          </div>
        )}

        {!loading && !error && photos.length === 0 && (
          <div className="photos-status">
            <p>No photos yet - check back soon.</p>
          </div>
        )}

        {!loading && !error && photos.length > 0 && (
          <div className="photos-masonry">
            {photos.map((key, i) => (
              <button
                key={key}
                className="photos-thumb"
                onClick={() => setLightbox(i)}
                aria-label={`View photo ${i + 1}`}
              >
                <img
                  src={`${BUCKET_URL}/${key}`}
                  alt={`Echo Hill photo ${i + 1}`}
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        )}
      </div>

      {lightbox !== null && (
        <div className="lightbox" onClick={() => setLightbox(null)}>
          <button className="lightbox-close" onClick={() => setLightbox(null)} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>

          <button
            className="lightbox-nav lightbox-nav--prev"
            onClick={e => { e.stopPropagation(); setLightbox(i => Math.max(i - 1, 0)); }}
            disabled={lightbox === 0}
            aria-label="Previous photo"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="28" height="28">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </button>

          <div className="lightbox-img-wrap" onClick={e => e.stopPropagation()}>
            <img
              src={`${BUCKET_URL}/${photos[lightbox]}`}
              alt={`Echo Hill photo ${lightbox + 1}`}
            />
          </div>

          <button
            className="lightbox-nav lightbox-nav--next"
            onClick={e => { e.stopPropagation(); setLightbox(i => Math.min(i + 1, photos.length - 1)); }}
            disabled={lightbox === photos.length - 1}
            aria-label="Next photo"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="28" height="28">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>

          <div className="lightbox-counter">
            {lightbox + 1} / {photos.length}
          </div>
        </div>
      )}
    </div>
  );
}
